import { useQuery } from "@tanstack/react-query";
import type { WorkspaceRole } from "@veritrellis/shared/roles";
import { apiFetch } from "../lib/api.js";
import {
  clearWorkspaceIdFromSession,
  readWorkspaceIdFromSession,
  writeWorkspaceIdToSession
} from "../lib/workspaceStorage.js";

export type { WorkspaceRole };

export type AdminMeData = {
  user: { id: string; email: string };
  workspaces: Array<{ id: string; name: string; slug: string; role: WorkspaceRole }>;
};

const fetchAdminMe = async (): Promise<AdminMeData> => {
  const res = await apiFetch("/admin/me");
  if (res.status === 401) {
    const next = `${globalThis.location.pathname}${globalThis.location.search}`;
    const returnTo = encodeURIComponent(next.startsWith("/") ? next : "/");
    globalThis.location.href = `/sign-in?next=${returnTo}`;
    throw new Error("unauthorized");
  }
  if (!res.ok) {
    throw new Error(`admin_me_failed_${String(res.status)}`);
  }
  return (await res.json()) as AdminMeData;
};

export const useAdminMe = () => {
  return useQuery({
    queryKey: ["admin", "me"],
    queryFn: async () => {
      const data = await fetchAdminMe();
      const stored = readWorkspaceIdFromSession();
      const ids = new Set(data.workspaces.map((w) => w.id));
      if (stored !== null && ids.has(stored)) {
        return { ...data, activeWorkspaceId: stored };
      }
      if (data.workspaces[0] !== undefined) {
        writeWorkspaceIdToSession(data.workspaces[0].id);
        return { ...data, activeWorkspaceId: data.workspaces[0].id };
      }
      clearWorkspaceIdFromSession();
      return { ...data, activeWorkspaceId: null as string | null };
    }
  });
};
